/**
 * Developer Users tab — lists distinct end-user IDs across teammates and runs.
 *
 * This is the default/index tab of the developer dashboard.
 * Fetches from v2 API endpoints and aggregates end-user stats.
 */

import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { env } from "@/env";
import { getAccessToken } from "@/lib/auth/storage";

export const Route = createFileRoute("/_dashboard/developer/")({
	component: DeveloperUsersPage,
});

interface EndUserRow {
	user_id: string;
	teammates: number;
	runs: number;
}

function useEndUsers() {
	return useQuery({
		queryKey: ["developer", "users"],
		queryFn: async () => {
			const token = getAccessToken();
			const baseUrl = env.VITE_API_BASE_URL;

			// Fetch teammates and runs in parallel to aggregate end-user stats
			const [teammatesRes, runsRes] = await Promise.all([
				fetch(`${baseUrl}/api/v2/teammates/`, {
					headers: { Authorization: `Bearer ${token}` },
				}),
				fetch(`${baseUrl}/api/v2/runs/`, {
					headers: { Authorization: `Bearer ${token}` },
				}),
			]);

			const teammates = (await teammatesRes.json()).data;
			const runs = (await runsRes.json()).data;

			// Aggregate by user_id
			const userMap = new Map<string, EndUserRow>();

			for (const t of teammates) {
				if (!t.user_id) continue;
				const row = userMap.get(t.user_id) || { user_id: t.user_id, teammates: 0, runs: 0 };
				row.teammates++;
				userMap.set(t.user_id, row);
			}

			for (const r of runs) {
				if (!r.user_id) continue;
				const row = userMap.get(r.user_id) || { user_id: r.user_id, teammates: 0, runs: 0 };
				row.runs++;
				userMap.set(r.user_id, row);
			}

			return Array.from(userMap.values());
		},
		staleTime: 30_000,
	});
}

function DeveloperUsersPage() {
	const { data: users, isLoading } = useEndUsers();

	if (isLoading) {
		return (
			<div className="space-y-3">
				<Skeleton className="h-10 w-full" />
				<Skeleton className="h-10 w-full" />
				<Skeleton className="h-10 w-full" />
			</div>
		);
	}

	if (!users?.length) {
		return (
			<div className="flex flex-col items-center justify-center gap-2 py-16 text-center">
				<p className="text-muted-foreground">No end-users found</p>
				<p className="text-sm text-muted-foreground">
					Pass <code className="rounded bg-muted px-1.5 py-0.5 text-xs">user_id</code> when creating
					teammates or runs to track end-users.
				</p>
			</div>
		);
	}

	return (
		<Table>
			<TableHeader>
				<TableRow>
					<TableHead>User ID</TableHead>
					<TableHead className="text-right">Teammates</TableHead>
					<TableHead className="text-right">Runs</TableHead>
				</TableRow>
			</TableHeader>
			<TableBody>
				{users.map((user) => (
					<TableRow key={user.user_id}>
						<TableCell>
							<Badge variant="outline" className="font-mono text-xs">
								{user.user_id}
							</Badge>
						</TableCell>
						<TableCell className="text-right">{user.teammates}</TableCell>
						<TableCell className="text-right">{user.runs}</TableCell>
					</TableRow>
				))}
			</TableBody>
		</Table>
	);
}
